import { Icon, Loader, Stack, Text, Tooltip } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { ALIAS_TEXT_COLORS } from 'packages/hotsauce/build/tokens';
import { DataLimitsCardStyles as S } from './DataLimitsCard.styles';

export enum WidgetVariant {
  WARNING = 'warning',
  SUCCESS = 'success',
  INFO = 'info',
}

interface DataLimitsCardProps {
  infoList: {
    label: string;
    value: string;
    valueColor?: keyof typeof ALIAS_TEXT_COLORS;
    popover?: string;
    testIdSpecifier?: string;
  }[];
  children: JSX.Element;
  widgetVariant: WidgetVariant;
  isLoading?: boolean;
}

export const DataLimitsCard = ({
  infoList,
  children,
  widgetVariant,
  isLoading,
}: DataLimitsCardProps): JSX.Element => {
  const { t } = useTranslationWithPrefix('billing:product-select-upsell-modal');

  return (
    <S.DataLimitsCard>
      <Stack direction="column">
        <Stack direction="column" spacing="x0">
          {infoList.map((info) => {
            return (
              <S.DataLimitsRow key={info.label}>
                <Text
                  textType="body2"
                  textColor="text_light"
                  data-testid={
                    info.testIdSpecifier ? `data-limits-${info.testIdSpecifier}-label` : undefined
                  }
                >
                  {info.label}
                </Text>
                <div>
                  <Text
                    textType="subtitle2"
                    textColor={info.valueColor || 'text_default'}
                    data-testid={
                      info.testIdSpecifier ? `data-limits-${info.testIdSpecifier}-value` : undefined
                    }
                  >
                    {info.value}
                  </Text>
                  {info.popover && (
                    <Tooltip
                      text={info.popover}
                      hideOnClick={false}
                      data-testid={
                        info.testIdSpecifier
                          ? `data-limits-${info.testIdSpecifier}-tooltip`
                          : undefined
                      }
                    >
                      <Icon
                        inline
                        color="text_light"
                        icon="info"
                        size="m"
                        css={{ marginLeft: '5px' }}
                      />
                    </Tooltip>
                  )}
                </div>
              </S.DataLimitsRow>
            );
          })}
        </Stack>
        <S.Widget
          warning={widgetVariant === WidgetVariant.WARNING}
          success={widgetVariant === WidgetVariant.SUCCESS}
          isLoading={!!isLoading}
        >
          {isLoading ? (
            <S.LoadingWidgetBody spacing="x1">
              <Loader isLoading size="m" />
              <Text>{t('data-limits-card.loading-label')}</Text>
            </S.LoadingWidgetBody>
          ) : (
            children
          )}
        </S.Widget>
      </Stack>
    </S.DataLimitsCard>
  );
};
